import { Await } from '@remix-run/react'
import Button from '../buttons/button'
import ContentTile from '../content/ContentTile'
import Modal from '../modalsAndOverlays/modal'
import Tag from '../badgesAndTags/tag'
import fallbackAvatarImage from '#/images/fallbackAvatar.webp'
import {
	TypographyCaps,
	TypographyDisplay,
	TypographyHeading,
	TypographyLabel,
	TypographyParagraph,
} from '../typography'
import {
	CaretDown,
	CaretLeft,
	CaretUp,
	EtherscanIcon,
	HelpIcon,
	MoreIcon,
	OpenSeaIcon,
	SignIcon,
	VerifiedIcon,
	XIcon,
} from '../icons'
import TransactionsTable from './transactionsTable.tsx'
import OffChain from '../common/offChain.tsx'
import ProofOfExhibition from '../common/proofOfExhibition.tsx'
import Image from '../content/Image'
import {
	asyncTruncateStringInMiddle,
	getNFTMarketplaceURL,
	getShareNFTToXURL,
	chainResources,
	isValidChain,
} from '#app/utils/misc.tsx'
import { Fragment, useEffect, useRef, useState, Suspense } from 'react'
import AnimateHeight from 'react-animate-height'
import AvatarPlaceholder from '#images/avatar11white.png'
import {
	AttestationType,
	collectionProfile,
	MetadataProperties,
	nft,
	NFTCache,
	nftCollectionInfo,
	sale,
} from '#types/loreTypes.tsx'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { SignModal } from '../common/signModal.tsx'
import LoreAI from '../common/LoreAI.tsx'

import InternalLink from '../common/internalLink.tsx'
import BannerSection from './bannerSection.tsx'
import LoreTutorial from './loreTutorial.tsx'
import { useTheme } from '#app/utils/providers/themeProvider'
import ImagePlaceholder from '#images/image-placeholder.jpg'
import ReactPlayer from '#app/components/content/Video'

const getNFTMarketplaceURl = (nftData: nft) => {
	if (
		nftData.chain == 'ethereum' ||
		nftData.chain == 'polygon' ||
		nftData.chain == 'base' ||
		nftData.chain == 'zora'
	)
		return `${chainResources[nftData.chain].marketplaceURL}${nftData.contract.address}/${nftData.tokenId}`
	if (nftData.chain == 'shape')
		return `${chainResources[nftData.chain].marketplaceURL}${nftData.contract.address}/t/${nftData.tokenId}`
	if (nftData.chain == 'solana') {
		if (nftData.token_info && nftData.token_info.supply > 1) {
			return `https://exchange.art/editions/${nftData.tokenId}`
		} else {
			return `https://exchange.art/single/${nftData.tokenId}`
		}
	}
	return ''
}

const getNFTMarketplaceIcon = (nftData: nft) => {
	if (nftData.chain && isValidChain(nftData.chain)) {
		const chain = nftData.chain.toLowerCase() as keyof typeof chainResources
		return chainResources[chain].marketplaceIcon
	}
	return <OpenSeaIcon />
}

const getNFTScanURL = (nftData: nft) => {
	if (nftData.chain == 'ethereum' || nftData.chain == 'polygon')
		return `${chainResources[nftData.chain].scanURL}/${nftData.contract.address}/${nftData.tokenId}`
	if (nftData.chain == 'base')
		return `${chainResources[nftData.chain].scanURL}/${nftData.contract.address}?a=${nftData.tokenId}`
	if (nftData.chain == 'shape')
		return `${chainResources[nftData.chain].scanURL}/${nftData.contract.address}/instance/${nftData.tokenId}`
	if (nftData.chain == 'solana')
		return `${chainResources[nftData.chain].scanURL}/${nftData.tokenId}?cluster=mainnet-alpha`
	if (nftData.chain == 'zora')
		return `${chainResources[nftData.chain].scanURL}/${nftData.contract.address}/instance/${nftData.tokenId}`
	if (nftData.chain == 'tezos')
		return `${chainResources[nftData.chain].scanURL}/${nftData.contract.address}/tokens/${nftData.tokenId}`
}

const getNFTScanIcon = (nft: nft) => {
	if (nft.chain && isValidChain(nft.chain)) {
		const chain = nft.chain.toLowerCase() as keyof typeof chainResources
		return chainResources[chain].scanIcon
	}
	return <EtherscanIcon />
}

export default function NFTProfile({
	collection,
	nft,
	nftCollectionInfo,
	mintedMetadata,
	salesData,
	ownersData,
	contractAddress,
	tokenId,
	nftSalesPageKey,
	origin,
	atomicLoreUrl,
	loreAIUrl,
	signModalParams,
	handleRevalidation,
}: {
	collection: collectionProfile
	nft: nft
	nftCollectionInfo: nftCollectionInfo
	mintedMetadata: AttestationType[]
	salesData: { sales: sale[]; salesPageKey: string }
	ownersData: string[]
	contractAddress: string
	tokenId: string
	nftSalesPageKey: string
	origin: string
	atomicLoreUrl: string
	loreAIUrl: string
	signModalParams: {
		isOpen: boolean
		templateFromURL: string
		cidMetadata: AttestationType
		matchingAddress: boolean
		isValidSign: boolean
		pendingPinFromURL: MetadataProperties | undefined
	}
	handleRevalidation: () => void
}) {
	const [signModalOpen, setSignModalOpen] = useState(
		signModalParams.isOpen ? signModalParams.isOpen : false,
	)
	const [isTutorialOpen, setIsTutorialOpen] = useState(false)

	return (
		<div className="mx-auto max-w-[1440px] px-4 py-16 md:px-12 lg:px-28">
			{(nft || (nft && nft.success) === false) && (
				<>
					<NavigationHeader nft={nft} atomicLoreUrl={atomicLoreUrl} />

					<NFTInfo
						nft={nft}
						collection={collection ? collection : nftCollectionInfo}
						salesData={salesData}
						ownersData={ownersData}
						setSignModalOpen={setSignModalOpen}
						setIsTutorialOpen={setIsTutorialOpen}
					/>
					<OffChain
						atomicLoreUrl={atomicLoreUrl}
						timeline={mintedMetadata}
						totalMetadata={
							mintedMetadata && mintedMetadata.length
								? mintedMetadata.length
								: 0
						}
					/>
					<ProofOfExhibition
						setSignModalOpen={setSignModalOpen}
						chain={nft.chain}
						contract={contractAddress}
						tokenId={tokenId}
						exhibitions={
							mintedMetadata
								? mintedMetadata.filter(
										(metadata: AttestationType) =>
											metadata.template.slug == 'proofOfExhibition',
									)
								: []
						}
					/>
					{/*<SuspenseTransactionHistory salesData={salesData} nft={nft} />*/}
					<LoreAISection
						contractAddress={contractAddress}
						tokenId={tokenId}
						setSignModalOpen={setSignModalOpen}
					/>
					<BannerSection />
					{isTutorialOpen && (
						<LoreTutorial
							isOpen={isTutorialOpen}
							onClose={() => setIsTutorialOpen(false)}
						/>
					)}
					<SignModal
						open={signModalOpen}
						onClose={() => {
							setSignModalOpen(false)
							handleRevalidation()
						}}
						template={signModalParams.templateFromURL}
						countersignEntry={signModalParams.cidMetadata}
						matchingAddress={signModalParams.matchingAddress}
						isValidSign={signModalParams.isValidSign}
						pendingPin={signModalParams.pendingPinFromURL}
						nft={nft}
						origin={origin}
						atomicLoreUrl={atomicLoreUrl}
					/>
				</>
			)}
			{(!nft || (nft && nft.success) === false) && (
				<TypographyHeading
					size="large"
					additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
				>
					{nft && nft.message ? nft.message : 'Error loading NFT'}
				</TypographyHeading>
			)}
		</div>
	)
}

const SuspenseTransactionHistory = ({
	salesData,
	nft,
}: {
	salesData: { sales: sale[] }
	nft: nft
}) => {
	return (
		<Suspense
			fallback={
				<TransactionHistory
					transactions={salesData ? salesData.sales : []}
					nft={nft}
					pageKey={''}
				/>
			}
		>
			<Await resolve={salesData}>
				{salesData => {
					return (
						<TransactionHistory
							transactions={salesData ? salesData.sales : []}
							nft={nft}
							pageKey={salesData.salesPageKey || ''}
						/>
					)
				}}
			</Await>
		</Suspense>
	)
}

const NavigationHeader = ({
	nft,
	atomicLoreUrl,
}: {
	nft: nft
	atomicLoreUrl: string
}) => {
	return (
		<div className="mb-6 flex h-12 px-0 sm:mb-12 md:flex-row md:items-center lg:px-12">
			<div className="flex flex-col items-center justify-around gap-y-4 pl-0 md:items-start lg:justify-evenly">
				<InternalLink to={'/'} prefetch="none">
					<Button variant="tertiary">
						<div className="flex items-center">
							<CaretLeft />
							Home
						</div>
					</Button>
				</InternalLink>
			</div>
			<div className="ml-auto mr-0 flex items-center justify-between space-x-4 lg:mt-0">
				<a href={getNFTScanURL(nft)} target="_blank" rel="noreferrer">
					<div className="flex h-12 w-12 items-center justify-center rounded-full border border-textPrimaryLight transition-colors hover:border-textPrimaryDark hover:bg-textPrimaryLight hover:text-textPrimaryDark dark:border-textPrimaryDark dark:text-textPrimaryDark dark:hover:border-textPrimaryLight dark:hover:bg-textPrimaryDark dark:hover:text-textPrimaryLight">
						{getNFTScanIcon(nft)}
					</div>
				</a>
				<a href={getNFTMarketplaceURL(nft)} target="_blank" rel="noreferrer">
					<div className="flex h-12 w-12 items-center justify-center rounded-full border border-textPrimaryLight transition-colors hover:border-textPrimaryDark hover:bg-textPrimaryLight hover:text-textPrimaryDark dark:border-textPrimaryDark dark:text-textPrimaryDark dark:hover:border-textPrimaryLight dark:hover:bg-textPrimaryDark dark:hover:text-textPrimaryLight">
						{getNFTMarketplaceIcon(nft)}
					</div>
				</a>
				<a
					href={getShareNFTToXURL(nft, atomicLoreUrl)}
					target="_blank"
					rel="noreferrer"
				>
					<div className="flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border border-textPrimaryLight transition-colors hover:border-textPrimaryDark hover:bg-textPrimaryLight hover:text-textPrimaryDark dark:border-textPrimaryDark dark:text-textPrimaryDark dark:hover:border-textPrimaryLight dark:hover:bg-textPrimaryDark dark:hover:text-textPrimaryLight">
						<XIcon />
					</div>
				</a>
			</div>
		</div>
	)
}

const NFTInfo = ({
	nft,
	collection,
	salesData,
	ownersData,
	setSignModalOpen,
	setIsTutorialOpen,
}: {
	nft: NFTCache
	collection: AttestationType | nftCollectionInfo
	salesData?: { sales: sale[]; salesPageKey: string }
	ownersData?: string[]
	setSignModalOpen: (open: boolean) => void
	setIsTutorialOpen: (open: boolean) => void
}) => {
	const descriptionRef = useRef(null)
	const [expanded, setExpanded] = useState(false)
	const [hasLongDescription, setHasLongDescription] = useState<
		boolean | undefined
	>(undefined)
	const [hasExpanded, setHasExpanded] = useState<boolean>(false)

	useEffect(() => {
		const isLongDescription = () => {
			if (!descriptionRef || !descriptionRef.current) return false
			//@ts-ignore
			const offsetHeight = descriptionRef.current.offsetHeight
			const scrollHeight = descriptionRef.current.scrollHeight
			const isTruncated =
				offsetHeight && scrollHeight && offsetHeight < scrollHeight
			if (isTruncated) {
				setHasLongDescription(true)
			} else {
				setHasLongDescription(false)
			}
			// return isTruncated;
		}
		isLongDescription()
	}, [])

	const toggleExpand = () => {
		if (!hasExpanded) setHasExpanded(true)
		setExpanded(!expanded)
	}

	const getDescriptionHeight = () => {
		if (hasLongDescription === undefined) return 'auto'
		if (hasLongDescription === false) return 'auto'
		if (hasLongDescription && expanded)
			return descriptionRef.current && descriptionRef.current.ScrollHeight
				? descriptionRef.current.ScrollHeight
				: 'auto'
		if (hasLongDescription && !expanded) return 120
		return 'auto'
	}
	const getNFTImageForHero = () => {
		if (
			nft.contract.address &&
			nft.contract.address.toLowerCase() ===
				'0xd4e4078ca3495de5b1d4db434bebc5a986197782'
		) {
			if (nft.image.originalUrl) return nft.image.originalUrl
		}
		if (nft.image) {
			if (typeof nft.image === 'string') {
				return nft.image
			} else if (nft.image.originalUrl) {
				return nft.image.originalUrl
			} else if (nft.image.cachedUrl) {
				return nft.image.cachedUrl
			}
			// } else {
			// 	if (nft.image.cachedUrl) {
			// 		return nft.image.cachedUrl
			// 	}
			// }
		}
		if (nft.previewImage) return nft.previewImage
		return ImagePlaceholder
	}

	return (
		<div
			className={`items-start justify-center px-0 md:flex md:flex-row lg:px-12`}
		>
			<div className={`h-full md:w-3/5`}>
				{(nft.image &&
					typeof nft.image !== 'string' &&
					nft.image.contentType &&
					nft.image.contentType.includes('video')) ||
				nft.video_url ? (
					<div className="relative w-full border border-solid border-grey300 pb-[100%]">
						<ReactPlayer nft={nft} />
					</div>
				) : (
					<Image
						className="absolute left-0 top-0 h-full w-full object-contain"
						containerClassName="relative w-full pb-[100%] border border-grey300 border-solid"
						imageUrl={getNFTImageForHero()}
						imageAlt={nft.name}
					></Image>
				)}
			</div>
			<div className="relative mt-auto h-full gap-y-4 pt-4 md:w-2/5 md:pl-16">
				{collection &&
				collection.references &&
				collection.references.attestationIPFSCID
					? collection &&
						collection.properties.name &&
						collection.properties.name !== '' && (
							<InternalLink
								to={`/collectionProfile/${collection.references.attestationIPFSCID}`}
							>
								<Button
									variant="secondary"
									rightIcon={<VerifiedIcon />}
									additionalClasses="rounded-full"
								>
									{collection.properties.name}
								</Button>
							</InternalLink>
						)
					: collection &&
						collection.name &&
						collection.name !== '' && (
							<Button
								variant="secondary"
								rightIcon={<VerifiedIcon />}
								additionalClasses="rounded-full"
							>
								{collection.name}
							</Button>
						)}
				<TypographyDisplay
					size="medium"
					additionalClasses="py-4 text-textPrimaryLight dark:text-textPrimaryDark break-words"
				>
					{nft.name}
				</TypographyDisplay>
				{nft &&
					nft.raw &&
					// @ts-ignore
					nft.raw.error && (
						<TypographyParagraph size="medium" additionalClasses="text-red">
							{nft.raw.error}
						</TypographyParagraph>
					)}
				<AnimateHeight
					id="description"
					duration={1000}
					height={getDescriptionHeight()} // see props documentation below
				>
					<div>
						<pre
							ref={descriptionRef}
							className={`font-abc text-paragraphMedium text-textSecondaryLight dark:text-textSecondaryDark whitespace-pre-wrap ${!hasExpanded ? 'line-clamp-5' : 'overflow-ellipsis'}`}
						>
							{nft.description}
						</pre>
					</div>
				</AnimateHeight>
				{hasLongDescription && (
					<Button
						variant="tertiary"
						leftIcon={expanded ? <CaretUp /> : <CaretDown />}
						onClick={toggleExpand}
					>
						{expanded ? 'Read Less' : 'Read More'}
					</Button>
				)}
				{collection && collection.artist && (
					<div className="flex w-full">
						<ContentTile
							additionalClasses={'px-0'}
							showBorder={false}
							// TODO: False until we have artist profile pages
							showCaret={false}
							variant="user"
							title={collection.artist.name}
							description="Artist"
							image={collection.artist.src || fallbackAvatarImage}
							isVerified={Boolean(collection.artist.verified)}
						/>
					</div>
				)}
				<NFTStatTable salesData={salesData} ownersData={ownersData} />
				<Button
					variant="primary"
					leftIcon={<SignIcon />}
					additionalClasses="w-full mt-4"
					onClick={() => setSignModalOpen(true)}
				>
					Sign
				</Button>
				<div
					className="group cursor-pointer"
					onClick={() => setIsTutorialOpen(true)}
				>
					<div className="group-hover:dark:textSecondaryDark mt-2 flex w-full items-center justify-center text-textPrimaryLight group-hover:text-textSecondaryLight dark:text-textPrimaryDark">
						<HelpIcon />
						<TypographyLabel
							size="medium"
							isBold={true}
							additionalClasses="ml-4 text-textPrimaryLight dark:text-textPrimaryDark group-hover:text-textSecondaryLight group-hover:dark:text-textSecondaryDark"
						>
							Learn More
						</TypographyLabel>
					</div>
				</div>
			</div>
		</div>
	)
}

const NFTStatTable = ({
	salesData,
	ownersData,
}: {
	salesData: { sales: sale[]; salesPageKey: string } | undefined
	ownersData: string[] | undefined
}) => {
	const [ownersModalOpen, setOwnersModalOpen] = useState(false)
	const [theme] = useTheme()
	return (
		<>
			<div className="mb-4 mt-8 box-content inline-block h-[111px] w-full max-w-5xl rounded-lg border border-textPrimaryLight align-middle dark:border-textPrimaryDark">
				<table className="min-w-full divide-y divide-textPrimaryLight dark:divide-textPrimaryDark">
					<thead>
						<tr className="divide-x divide-textPrimaryLight dark:divide-textPrimaryDark">
							<th
								scope="col"
								className="w-1/2 py-3.5 pl-4 pr-4 text-center text-sm font-semibold text-textSecondaryLight dark:text-textSecondaryDark"
							>
								<TypographyCaps size="small">Owners</TypographyCaps>
							</th>
							<th
								scope="col"
								className="w-1/2 px-4 py-3.5 text-center text-sm font-semibold text-textSecondaryLight dark:text-textSecondaryDark"
							>
								<TypographyCaps size="small">Last sold</TypographyCaps>
							</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-textPrimaryLight dark:divide-textPrimaryDark">
						<tr className="divide-x divide-textPrimaryLight dark:divide-textPrimaryDark">
							<td className="w-1/2 items-center justify-center whitespace-nowrap py-4 pl-4 pr-4 text-center text-sm font-medium text-textPrimaryLight dark:text-textPrimaryDark">
								{ownersData && ownersData.length > 1 ? (
									<Button
										variant="tertiary"
										additionalClasses="w-full text-center items-center justify-center"
										onClick={() => setOwnersModalOpen(true)}
									>
										<TypographyParagraph size="large" isBold={true}>
											{ownersData && ownersData.length ? (
												ownersData.length
											) : (
												<SkeletonTheme
													baseColor={
														theme === 'light' || theme === undefined
															? '#f5f5f5'
															: '#383838'
													}
													highlightColor={
														theme === 'light' || theme === undefined
															? '#f8f8f8'
															: '#212121'
													}
												>
													<Skeleton />
												</SkeletonTheme>
											)}
										</TypographyParagraph>
									</Button>
								) : ownersData && ownersData[0] ? (
									<Suspense
										fallback={
											<TypographyParagraph size="large" isBold={true}>
												...
											</TypographyParagraph>
										}
									>
										<Await
											resolve={(async () =>
												await asyncTruncateStringInMiddle(ownersData[0]))()}
										>
											{(ownerStr: string) => {
												return (
													<TypographyParagraph size="large" isBold={true}>
														{ownerStr}
													</TypographyParagraph>
												)
											}}
										</Await>
									</Suspense>
								) : (
									0
								)}
							</td>
							<td className="w-1/2 whitespace-nowrap py-4 pl-4  pr-4 text-center text-sm font-medium text-textPrimaryLight dark:text-textPrimaryDark">
								<Suspense
									fallback={
										<TypographyParagraph size="large" isBold={true}>
											Loading
										</TypographyParagraph>
									}
								>
									<Await resolve={salesData}>
										{salesData => {
											if (
												salesData &&
												salesData.sales &&
												salesData.sales.find((sale: sale) => sale.price)
											) {
												return (
													<TypographyParagraph size="large" isBold={true}>
														{salesData.sales
															.find((sale: sale) => sale.price)
															?.price?.toString()
															.slice(0, 12) +
															' ' +
															salesData.sales
																.find((sale: sale) => sale.price)
																?.symbol?.toUpperCase()}
													</TypographyParagraph>
												)
											} else {
												return (
													<TypographyParagraph size="large" isBold={true}>
														Unknown
													</TypographyParagraph>
												)
											}
										}}
									</Await>
								</Suspense>
							</td>
						</tr>
					</tbody>
				</table>
			</div>

			<Modal
				size="large"
				title="Owners"
				body={
					<Fragment>
						{ownersData &&
							ownersData.map((owner: string) => {
								let ownerLink = `https://etherscan.io/address/${owner}`
								return (
									<div
										key={owner}
										className="border-b border-dotted border-textSecondaryLight"
									>
										<a href={ownerLink} target="_blank">
											<Suspense
												fallback={
													<ContentTile
														variant="user"
														showCaret={true}
														showBorder={false}
														title={'...'}
														description={'1 items'}
														image={AvatarPlaceholder}
														isVerified={false}
													/>
												}
											>
												<Await
													resolve={(async () =>
														await asyncTruncateStringInMiddle(owner))()}
												>
													{(ownerStr: string) => (
														<ContentTile
															variant="user"
															showCaret={true}
															showBorder={false}
															title={ownerStr}
															description={'1 items'}
															image={AvatarPlaceholder}
															isVerified={false}
														/>
													)}
												</Await>
											</Suspense>
										</a>
									</div>
								)
							})}
					</Fragment>
				}
				isOpen={ownersModalOpen}
				onClose={e => setOwnersModalOpen(false)}
			/>
		</>
	)
}

const TransactionHistory = ({
	transactions,
	nft,
	pageKey,
}: {
	transactions: sale[]
	nft: nft
	pageKey: string
}) => {
	const [transactionsList, setTransactionsList] = useState(transactions)
	const [currentPageKey, setCurrentPageKey] = useState(pageKey)
	const [loading, setLoading] = useState(false)

	const loadTransactions = async () => {
		setLoading(true)
		try {
			const options = {
				method: 'GET',
				headers: { 'Content-Type': 'application/json' },
			}
			const response = await fetch(
				`/nftSalesAPI?contract=${nft.contract.address}&tokenId=${nft.tokenId}&limit=10&pageKey=${pageKey}`,
				options,
			)
			const newTransactions = (await response.json()) as {
				sales: sale[]
				pageKey: string
			}
			setTransactionsList([...transactionsList, ...newTransactions.sales])
			setCurrentPageKey(newTransactions.pageKey)
		} catch (e) {
			console.error(e)
		}
		setLoading(false)
	}
	const nftChain = nft.chain || 'ethereum'

	return (
		<div className="my-24 flex w-full flex-col gap-y-4 px-0 lg:px-12">
			<div className="flex w-full justify-between">
				<div className="flex flex-1 items-center gap-x-4">
					<TypographyHeading
						size="medium"
						isBold={true}
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
					>
						Transaction history
					</TypographyHeading>
				</div>
			</div>
			<div className="pt-6">
				{transactionsList && transactionsList.length ? (
					<TransactionsTable
						chain={nftChain}
						data={transactionsList}
						showButton={false}
					/>
				) : (
					<div className="mt-12 flex flex-col items-center justify-center">
						<TypographyParagraph
							size="medium"
							additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
						>
							No transactions have been recorded yet.
						</TypographyParagraph>
					</div>
				)}
			</div>
			{!loading && currentPageKey && (
				<div className="flex w-full items-center justify-center">
					<Tag showIcon={false} onClick={() => loadTransactions()}>
						<MoreIcon />
					</Tag>
				</div>
			)}
		</div>
	)
}

const LoreAISection = ({
	contractAddress,
	tokenId,
	setSignModalOpen,
}: {
	contractAddress: string
	tokenId: string
	setSignModalOpen: (open: boolean) => void
}) => {
	return (
		<div className="my-24 w-full px-0 lg:px-12">
			<LoreAI
				onMessageReceived={data => console.log('AI response:', data)}
				contractHash={contractAddress}
				tokenId={tokenId}
				setSignModalOpen={setSignModalOpen}
			/>
		</div>
	)
}
